import { normalizeProviderSurfaceKey, providerSurfaceLabel } from "./providerSurfaceRegistry";

const object = (value) => value && typeof value === "object" && !Array.isArray(value) ? value : {};
export const providerMetricNumber = (value) => value === null || value === undefined || (typeof value === "string" && value.trim() === "") || typeof value === "boolean" ? null : Number.isFinite(Number(value)) ? Number(value) : null;

// Provider spend is never inferred from an inventory channel or marked up in a portal.
export const governedProviderSpend = (entry = {}) => {
  const metrics = object(entry.metrics);
  const basis = entry.spendBasis || entry.spend_basis || metrics.spendBasis || metrics.spend_basis;
  const currency = entry.currency || entry.spendCurrency || metrics.currency || metrics.spendCurrency;
  const available = entry.spendAvailable ?? metrics.spendAvailable;
  const authority = entry.financialAuthority || metrics.financialAuthority;
  const governedBilledBasis = basis === "client_billed" || (
    basis === "commercial_model_derived" && authority?.contract === "campaign_financial_basis_v1" && authority?.available === true
  );
  if (available === false || !governedBilledBasis || !/^[A-Z]{3}$/.test(currency || "")) return null;
  return providerMetricNumber(entry.clientFacingSpend ?? metrics.clientFacingSpend ?? entry.spend ?? metrics.spend);
};

export const providerEvidenceRows = (payload = {}) => {
  if (payload?.kpiSourceType && payload.kpiSourceType !== "canonical_current") return [];
  const summary = payload?.canonicalMetrics?.summary || payload?.canonicalReporting || payload?.canonicalSummary || {};
  /*
   * Channel × execution provider: once provider delivery is folded into Omni
   * Channels, a provider's own figures live in the `byProvider` projection —
   * the provider-keyed bucket holds only what has no channel yet. The
   * projection is read first; an older payload behaves exactly as before.
   */
  const foldedProviders = summary.omniProviderFold?.applied === true ? object(summary.byProvider) : {};
  const canonicalMap = summary.platforms || summary.byChannel || {};
  const map = payload?.platformPerformanceByChannel || (Object.keys(foldedProviders).length ? { ...object(canonicalMap), ...foldedProviders } : canonicalMap);
  const scopedProviderKeys = new Set([
    ...(summary.platformScope?.keys || []),
    ...Object.keys(foldedProviders),
    ...Object.keys(object(summary.byChannel)),
    ...Object.keys(object(summary.platforms)),
  ].map(normalizeProviderSurfaceKey).filter(Boolean));
  const rows = new Map();
  Object.entries(object(map)).forEach(([rawKey, entry]) => {
    const key = normalizeProviderSurfaceKey(rawKey);
    if (!key || rows.has(key) || entry?.aggregationRole === "channel_breakdown") return;
    if (!scopedProviderKeys.has(key) && entry?.hasCanonicalMetrics !== true && entry?.reportingEvidence?.savedReportingAvailable !== true) return;
    const metrics = { ...object(entry.metrics), ...object(entry) };
    const provenance = entry.provenance || entry.sourceProvenance || entry.reportingProvenance || entry.reportingEvidence || {};
    rows.set(key, {
      key,
      label: providerSurfaceLabel(key),
      spend: governedProviderSpend(entry),
      currency: metrics.currency || metrics.spendCurrency || null,
      impressions: providerMetricNumber(metrics.impressions),
      clicks: providerMetricNumber(metrics.clicks),
      source: entry.sourceLabel || metrics.sourceLabel || provenance.source || (entry.hasCanonicalMetrics === true || entry.reportingEvidence?.savedReportingAvailable === true ? "Saved canonical reporting" : "Reporting unavailable"),
      sourceReportedAt: entry.sourceReportedAt || entry.dataFreshness?.sourceReportedAt || entry.dataFreshness?.timestamp || provenance.sourceReportedAt || provenance.sourceReadAt || null,
      status: entry.status || entry.reportingStatus || (entry.hasCanonicalMetrics ? "saved_reporting" : "unavailable"),
      provenance,
      basisProvenance: entry.basisProvenance || null,
      financialAuthority: entry.financialAuthority || null,
      reportingEvidence: entry.reportingEvidence || null,
    });
  });
  return ["yahoo_dsp", "viant"].filter((key) => rows.has(key)).map((key) => rows.get(key));
};

export const formatProviderCurrency = (value, currency) => {
  if (providerMetricNumber(value) === null || !/^[A-Z]{3}$/.test(currency || "")) return "Unavailable";
  try { return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(Number(value)); }
  catch { return "Unavailable"; }
};
